import React from 'react';
import { percent, percentExact, count } from './pricingFormat';

/**
 * How an Airbnb discount, promotion or account rule reads — shared by the pricing card
 * «الخصومات والعروض الحالية» and the night popup on «التقويم» (tasks/ops-calendar-airbnb-offers.md),
 * so the two never word the same offer differently.
 *
 * Every helper takes `t`: the card passes the user's `useLanguage().t` (the pricing page is
 * bilingual), the Arabic-only grid passes a fixed Arabic lookup over the same locale keys.
 */

// Every figure is isolated left-to-right: inside an Arabic line «15%» flips to «%15» and «+15%» to
// «15%+» (I18N_STRATEGY.md:214, numberFormatter.js).
export const Figure = ({ children }) => <bdi dir="ltr" data-figure>{children}</bdi>;

export const STAY = 'STAYED_AT_LEAST_X_DAYS';
export const LAST_MINUTE = 'BOOKED_WITHIN_AT_MOST_X_DAYS';
export const EARLY_BIRD = 'BOOKED_BEYOND_AT_LEAST_X_DAYS';

export const ruleName = (type, days, t) => {
  if (type === STAY) {
    if (days === 7) return t('pricing.offers.weekly');
    if (days === 28) return t('pricing.offers.monthly');
    return t('pricing.offers.stayAtLeast');
  }
  if (type === LAST_MINUTE) return t('pricing.offers.lastMinute');
  if (type === EARLY_BIRD) return t('pricing.offers.earlyBird');
  return type;
};

/**
 * A count with its noun in the right form: «ليلة» · «ليلتان» · «7 ليالٍ» · «28 ليلة». The forms live in
 * the locale (one / two / few = 3–10 / many = 11+, the same split as utils/alertDisplay.js
 * `arabicPlural`); Arabic says the dual without a figure.
 */
const quantity = (n, forms, isArabic) => {
  if (isArabic && n === 2) return forms.two;
  const noun = n === 1 ? forms.one : n === 2 ? forms.two : n <= 10 ? forms.few : forms.many;
  return <><Figure>{count(n)}</Figure> {noun}</>;
};

export const ruleDetail = (type, days, t, isArabic) => {
  if (typeof days !== 'number') return null;
  if (type === STAY) {
    return <>{quantity(days, t('pricing.offers.nightForms'), isArabic)} {t('pricing.offers.orMore')}</>;
  }
  if (type === LAST_MINUTE || type === EARLY_BIRD) {
    const bound = type === LAST_MINUTE ? t('pricing.offers.orLess') : t('pricing.offers.orMore');
    return <>{t('pricing.offers.bookedBefore')} {quantity(days, t('pricing.offers.dayForms'), isArabic)} {bound}</>;
  }
  return null;
};

// A promotion type Airbnb adds later has no translation yet — show its raw name, never the locale key.
export const promotionName = (type, t) => {
  const key = `pricing.offers.promotion.${type}`;
  const label = t(key);
  return label === key ? type : label;
};

/** A running promotion's name and fixed percentage, e.g. «عرض الإعلان الجديد 20%». */
export const promotionText = (promotion, t) => (
  <>
    {promotionName(promotion.type, t)}
    {typeof promotion.percent === 'number' && <> <Figure>{percent(promotion.percent)}</Figure></>}
  </>
);

export const accountRuleText = (adjustment, t) => {
  const sign = adjustment.mode === 'increase_by_percent' ? '+' : adjustment.mode === 'decrease_by_percent' ? '−' : null;
  // Any other Rate Logic mode is named in Arabic, its raw value kept for whoever checks Channex.
  if (!sign) return <>{t('pricing.offers.otherAccountRule')} <Figure>{adjustment.mode} {adjustment.value ?? ''}</Figure></>;
  if (typeof adjustment.value !== 'number') return t('pricing.offers.accountRuleUnknownValue');
  return <><Figure>{sign}{percentExact(adjustment.value)}</Figure> {t('pricing.offers.accountRule')}</>;
};

// A discount in percent, or — for a format Airbnb has not used so far — the raw figure, said so.
export const ruleValue = (rule, t) => (typeof rule.percent === 'number'
  ? <Figure>{percentExact(rule.percent)}</Figure>
  : <>{t('pricing.offers.unknownRuleFormat')} <Figure>{rule.change} {rule.changeType}</Figure></>);
